import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useApplicationStore from '../../store/applicationStore';
import PersonalDetailsStep from './PersonalDetailsStep';
import ProfessionalDetailsStep from './ProfessionalDetailsStep';
import DocumentUploadStep from './DocumentUploadStep';
import '../../assets/css/main.css';
import './Application.css';
const ApplicationJourneyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Zustand store
  const {
    leadData,
    currentStep: storeCurrentStep,
    setCurrentStep
  } = useApplicationStore();

  const [currentStep, setCurrentStepLocal] = useState(storeCurrentStep || 1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    { 
      id: 1, 
      title: 'Personal Details', 
      component: PersonalDetailsStep,
      hash: 'personaldetails',
      pageTitle: 'Personal Loan Application - Personal Details - SnapFunds'
    },
    { 
      id: 2, 
      title: 'Professional Details', 
      component: ProfessionalDetailsStep,
      hash: 'professionaldetails',
      pageTitle: 'Personal Loan Application - Professional Details - SnapFunds'
    }
    // { 
    //   id: 3, 
    //   title: 'Bank Details & Docs', 
    //   component: DocumentUploadStep,
    //   hash: 'documents',
    //   pageTitle: 'Document Upload - Personal Loan Application | Snapfunds'
    // } // Commented out for now
  ];

  // Function to get step from hash
  const getStepFromHash = (hash) => {
    const step = steps.find(s => s.hash === hash);
    return step ? step.id : 1;
  };

  // Function to update page title based on current step
  const updatePageTitle = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    if (step) {
      document.title = step.pageTitle;
    }
  };

  // Function to update URL hash
  const updateUrlHash = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    if (step) {
      const newUrl = `/application/journey#${step.hash}`;
      window.history.replaceState(null, '', newUrl);
    }
  };

  // Initialize current step
  useEffect(() => {
    // Check if lead data exists, if not redirect to eligibility page
    if (!leadData) {
      console.log('No lead data found, redirecting to eligibility page');
      navigate('/');
      return;
    }
    
    let initialStep = 1;
    
    // First priority: Check URL hash
    const hash = location.hash.substring(1); // Remove the # symbol
    if (hash) {
      const stepFromHash = getStepFromHash(hash);
      if (stepFromHash) {
        initialStep = stepFromHash;
        console.log('Using step from URL hash:', initialStep);
      }
    } else {
      // Second priority: Try to restore current step from localStorage
      const savedStep = localStorage.getItem(`applicationStep_${leadData.id}`);
      
      if (savedStep) {
        // Use saved step if it's valid
        const parsedStep = parseInt(savedStep);
        if (parsedStep >= 1 && parsedStep <= steps.length) {
          initialStep = parsedStep;
          console.log('Restored step from localStorage:', initialStep);
        }
      } else {
        // Third priority: Determine initial step based on application stage
        if (leadData.eligibility && leadData.eligibility.applicationStage) {
          const stage = leadData.eligibility.applicationStage;
          if (stage === 'leadCreated' || stage === 'personalDetailsFilled'){
            initialStep = 1;
          } else if (stage === 'preApprovalCheck'){
            initialStep = 2;
          } 
           else if (stage === 'employmentDetailsFilled') {
            initialStep = 2; // Professional Details
          } 
        }
      }
    }
    
    setCurrentStepLocal(initialStep);
    setCurrentStep(initialStep);
    
    // Update page title and URL hash
    updatePageTitle(initialStep);
    updateUrlHash(initialStep);
    
    console.log('Lead data loaded from store:', leadData);
    console.log('Application stage:', leadData.eligibility?.applicationStage);
    console.log('Starting application journey from step:', initialStep);
  }, [leadData, setCurrentStep, navigate, location.hash]);

  // Sync local state when store currentStep changes
  useEffect(() => {
    if (storeCurrentStep && storeCurrentStep !== currentStep) {
      setCurrentStepLocal(storeCurrentStep);
    }
  }, [storeCurrentStep, currentStep]);

  // Cleanup function to remove localStorage entry when component unmounts
  useEffect(() => {
    return () => {
      // Only cleanup if we're not navigating to thank you page
      if (leadData?.id && !window.location.pathname.includes('/thank-you')) {
        // Keep the step saved for refresh scenarios
        console.log('ApplicationJourneyPage unmounting, keeping step saved for potential refresh');
      }
    };
  }, [leadData?.id]);

  // Navigation functions
  const nextStep = () => {
    if (currentStep < steps.length) {
      const newStep = currentStep + 1;
      setCurrentStepLocal(newStep);
      setCurrentStep(newStep);
      
      // Update page title and URL hash
      updatePageTitle(newStep);
      updateUrlHash(newStep);
      
      // Save to localStorage
      if (leadData?.id) {
        localStorage.setItem(`applicationStep_${leadData.id}`, newStep.toString());
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setCurrentStepLocal(newStep);
      setCurrentStep(newStep);
      
      // Update page title and URL hash
      updatePageTitle(newStep);
      updateUrlHash(newStep);
      
      // Save to localStorage
      if (leadData?.id) {
        localStorage.setItem(`applicationStep_${leadData.id}`, newStep.toString());
      }
    }
  };

  const editStep = (stepNumber) => {
    setCurrentStepLocal(stepNumber);
    setCurrentStep(stepNumber);
    
    // Update page title and URL hash
    updatePageTitle(stepNumber);
    updateUrlHash(stepNumber);
    
    // Save to localStorage
    if (leadData?.id) {
      localStorage.setItem(`applicationStep_${leadData.id}`, stepNumber.toString());
    }
  };

  // Render current step
  const renderCurrentStep = () => {
    const step = steps.find(s => s.id === currentStep);
    if (!step) return null;

    const StepComponent = step.component;
    
    return (
      <StepComponent 
        onNext={nextStep}
        onPrev={prevStep}
        onEditStep={editStep}
        isSubmitting={isSubmitting}
      />
    );
  };

  return (
    <>
      <section className="onboard-container">
        <h1 className="onboard-title" style={{ color: '#283782' }}>Your personalized offer is just two steps away …</h1>

      {/* Progress */}
      <div className="onboard-progress">
      {steps.map((step, index) => (
        <div key={step.id} className={`progress-step ${index <= currentStep - 1 ? 'active' : ''}`}>
          {step.title}
        </div>
        ))}
      </div>

    {/* Step Content */}
    {renderCurrentStep()}
    </section>
    </>
  );
};

export default ApplicationJourneyPage;
